import React, { useState, useEffect, useRef } from 'react';
import { PlusCircle, Trash2, AlertCircle, Info, Loader2, Save, Pencil, X, ImagePlus, Check } from 'lucide-react';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';

interface DashboardProps {
  isDarkMode: boolean;
  language: string;
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'important';
  created_at: string;
  youtube_url?: string;
  images?: string[];
}

export function Dashboard({ isDarkMode, language }: DashboardProps) {
  const t = translations[language as keyof typeof translations];
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [readAnnouncements, setReadAnnouncements] = useState<Set<string>>(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    type: 'info' as 'info' | 'important',
    youtube_url: '',
    images: [] as string[]
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    loadAnnouncements();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      setIsAdmin(profile?.role === 'admin');
    } catch (err) {
      console.error('Error checking admin status:', err);
    }
  };

  const loadAnnouncements = async () => {
    try {
      setIsLoading(true);
      
      // Get all announcements
      const { data: announcements, error: announcementsError } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (announcementsError) throw announcementsError;

      // Get read announcements for current user
      const { data: readStatus, error: readError } = await supabase
        .from('read_announcements')
        .select('announcement_id');

      if (readError) throw readError;

      // Create set of read announcement IDs
      const readSet = new Set((readStatus || []).map(status => status.announcement_id));
      setReadAnnouncements(readSet);
      setAnnouncements(announcements || []);
    } catch (err) {
      console.error('Error loading announcements:', err);
      setError(err instanceof Error ? err.message : 'Failed to load announcements');
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (announcementId: string) => {
    try {
      const { error } = await supabase
        .from('read_announcements')
        .insert({
          announcement_id: announcementId,
          user_id: (await supabase.auth.getUser()).data.user?.id
        })
        .single();

      if (error) throw error;

      setReadAnnouncements(prev => new Set([...prev, announcementId]));
    } catch (err) {
      console.error('Error marking announcement as read:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setError('User not authenticated');
        return;
      }

      const embedUrl = getYouTubeEmbedUrl(newAnnouncement.youtube_url);

      if (editingAnnouncement) {
        const { error: updateError } = await supabase
          .from('announcements')
          .update({
            title: newAnnouncement.title,
            content: newAnnouncement.content,
            type: newAnnouncement.type,
            youtube_url: embedUrl || null,
            images: newAnnouncement.images
          })
          .eq('id', editingAnnouncement.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('announcements')
          .insert([{
            ...newAnnouncement,
            created_by: session.user.id,
            youtube_url: embedUrl || null,
            images: newAnnouncement.images
          }]);

        if (insertError) throw insertError;
      }

      setNewAnnouncement({ 
        title: '', 
        content: '', 
        type: 'info',
        youtube_url: '',
        images: []
      });
      setEditingAnnouncement(null);
      setShowForm(false);
      loadAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to save announcement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setNewAnnouncement({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      youtube_url: announcement.youtube_url || '',
      images: announcement.images || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      loadAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete announcement');
    }
  };

  const handleImageUpload = async (files: FileList) => {
    try {
      setUploadingImages(true);
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) {
          continue;
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `announcements/${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from('avatars')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setNewAnnouncement(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (err) {
      console.error('Error uploading images:', err);
      setError('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageRemove = (index: number) => {
    setNewAnnouncement(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    
    try {
      let videoId = '';
      
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1];
      } else if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(new URL(url).search);
        videoId = urlParams.get('v') || '';
      } else if (url.includes('youtube.com/embed/')) {
        return url;
      }
      
      videoId = videoId.split('&')[0];
      videoId = videoId.split('?')[0];
      
      if (!videoId) return '';
      
      return `https://www.youtube.com/embed/${videoId}`;
    } catch (err) {
      console.error('Error parsing YouTube URL:', err);
      return '';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = t.months[date.getMonth()];
    const year = date.getFullYear();
    const weekDay = t.days[date.getDay()];

    return t.date_format
      .replace('{day}', weekDay)
      .replace('{date}', day.toString())
      .replace('{month}', month)
      .replace('{year}', year.toString());
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="flex-1 overflow-y-auto p-8 pt-20 lg:pt-8">
        <h1 className={`text-2xl font-semibold mb-6 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>
          {t.dashboard}
        </h1>

        {error && (
          <div className="mb-6 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
            {error}
          </div>
        )}

        <div className={`p-6 rounded-lg border mb-6 ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>{t.announcements}</h2>
            
            {isAdmin && (
              <button
                onClick={() => {
                  setShowForm(!showForm);
                  if (!showForm) {
                    setEditingAnnouncement(null);
                    setNewAnnouncement({ 
                      title: '', 
                      content: '', 
                      type: 'info',
                      youtube_url: '',
                      images: []
                    });
                  }
                }}
                className="flex items-center gap-2 text-orange-500 hover:text-orange-600 transition-colors"
              >
                <PlusCircle size={16} />
                <span className="text-sm font-medium">{t.new_announcement}</span>
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {isAdmin && showForm && (
                <form onSubmit={handleSubmit} className="mb-6 space-y-4">
                  <input
                    type="text"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    placeholder={t.announcement_title}
                    className={`w-full rounded-lg px-4 py-2 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    required
                  />
                  <textarea
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    placeholder={t.announcement_content}
                    className={`w-full rounded-lg px-4 py-2 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    rows={4}
                    required
                  />
                  <input
                    type="url"
                    value={newAnnouncement.youtube_url}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, youtube_url: e.target.value })}
                    placeholder="YouTube URL (optional)"
                    className={`w-full rounded-lg px-4 py-2 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  />
                  <div className="space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          handleImageUpload(e.target.files);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImages}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
                        isDarkMode
                          ? 'bg-[#2b2b2b] text-gray-200 hover:bg-[#333333]'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {uploadingImages ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <ImagePlus size={14} />
                          <span>Upload Images</span>
                        </>
                      )}
                    </button>
                    {newAnnouncement.images.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {newAnnouncement.images.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt=""
                              className="w-20 h-20 object-cover rounded cursor-pointer"
                              onClick={() => setSelectedImage(url)}
                            />
                            <button
                              type="button"
                              onClick={() => handleImageRemove(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <select
                      value={newAnnouncement.type}
                      onChange={(e) => setNewAnnouncement({ 
                        ...newAnnouncement, 
                        type: e.target.value as 'info' | 'important' 
                      })}
                      className={`rounded-lg px-4 py-2 text-sm ${
                        isDarkMode
                          ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                          : 'bg-gray-50 text-gray-900 border-gray-200'
                      } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    >
                      <option value="info">{t.info}</option>
                      <option value="important">{t.important}</option>
                    </select>
                    <button
                      type="submit"
                      disabled={isSaving || uploadingImages}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          <span>{t.saving}</span>
                        </>
                      ) : (
                        <>
                          <Save size={14} strokeWidth={2.5} />
                          <span>{editingAnnouncement ? t.save_changes : t.publish}</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {announcements.length === 0 ? (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t.no_announcements}
                </p>
              ) : (
                announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className={`p-4 rounded-lg ${
                      announcement.type === 'important'
                        ? 'bg-red-50 border border-red-200'
                        : isDarkMode
                          ? 'bg-[#2b2b2b]'
                          : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-2">
                        {announcement.type === 'important' ? (
                          <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                        ) : (
                          <Info size={16} className={`${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          } flex-shrink-0`} />
                        )}
                        <h3 className={`font-medium ${
                          announcement.type === 'important'
                            ? 'text-red-700'
                            : isDarkMode
                              ? 'text-gray-200'
                              : 'text-gray-900'
                        }`}>
                          {announcement.title}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {!readAnnouncements.has(announcement.id) && (
                          <button
                            onClick={() => markAsRead(announcement.id)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              announcement.type === 'important'
                                ? 'text-red-600 hover:bg-red-100'
                                : isDarkMode
                                  ? 'text-gray-400 hover:bg-[#3b3b3b]'
                                  : 'text-gray-500 hover:bg-gray-100'
                            }`}
                            title={t.mark_as_read}
                          >
                            <Check size={14} strokeWidth={2.5} />
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleEdit(announcement)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                announcement.type === 'important'
                                  ? 'text-red-600 hover:bg-red-100'
                                  : isDarkMode
                                    ? 'text-gray-400 hover:bg-[#3b3b3b]'
                                    : 'text-gray-500 hover:bg-gray-100'
                              }`}
                            >
                              <Pencil size={14} strokeWidth={2.5} />
                            </button>
                            <button
                              onClick={() => handleDelete(announcement.id)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                announcement.type === 'important'
                                  ? 'text-red-600 hover:bg-red-100'
                                  : isDarkMode
                                    ? 'text-gray-400 hover:bg-[#3b3b3b]'
                                    : 'text-gray-500 hover:bg-gray-100'
                              }`}
                            >
                              <Trash2 size={14} strokeWidth={2.5} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`mt-2 text-sm ${
                      announcement.type === 'important'
                        ? 'text-red-500'
                        : isDarkMode
                          ? 'text-gray-500'
                          : 'text-gray-400'
                    }`}>
                      {formatDate(announcement.created_at)}
                    </div>
                    <p className={`mt-2 text-sm whitespace-pre-line ${
                      announcement.type === 'important'
                        ? 'text-red-600'
                        : isDarkMode
                          ? 'text-gray-400'
                          : 'text-gray-600'
                    }`}>
                      {announcement.content}
                    </p>
                    {announcement.youtube_url && (
                      <div className="mt-4 aspect-video w-full rounded-lg overflow-hidden">
                        <iframe 
                          className="w-full h-full"
                          src={announcement.youtube_url}
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                      </div>
                    )}
                    {announcement.images && announcement.images.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {announcement.images.map((url, index) => (
                          <img
                            key={index}
                            src={url}
                            alt=""
                            className="w-32 h-32 object-cover rounded cursor-pointer"
                            onClick={() => setSelectedImage(url)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={selectedImage}
              alt=""
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-opacity"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}